import React from 'react';
import { useFormik } from 'formik';
import dayjs from 'dayjs';
import { string, object } from 'yup';
import styled from 'styled-components';
import { Box, Grid } from '@mui/material';
import { PAGE_TEXT } from '../lang';
import {
  type MeritFormValues,
  type FormikProp,
} from '../../../graphql/ApplicationForm/merit/types';
import {
  Select,
  TextField,
  Button,
  DatePicker,
  type SelectOption,
} from '../../../../../components';
import { handleReadySubmit } from '../../../../../utils';

export type MeritBoxFormProps = {
  initialValues: MeritFormValues;
  enableEdit: boolean;
  meritIndex: number;
  enableDelete: boolean;
  countryList: SelectOption[];
  onSubmit: (values: MeritFormValues) => void;
  handleDelete: () => void;
};
const pageText = PAGE_TEXT.OTHER_INFORMATION.MERIT;
export const MeritBoxForm: React.FunctionComponent<
  MeritBoxFormProps
> = props => {
  const {
    initialValues,
    enableEdit,
    meritIndex,
    enableDelete,
    countryList: nationalityOptions,
    onSubmit,
  } = props;

  const formik: FormikProp = useFormik<MeritFormValues>({
    initialValues,
    validationSchema: object({
      awardDate: string().required('Required'),
      meritName: string().required('Required'),
      country: string().required('Required'),
      awardBodyName: string().required('Required'),
      attainmentMeans: string().required('Required'),
    }),
    onSubmit,
  });

  return (
    <Container>
      <form onSubmit={handleReadySubmit(formik, meritIndex)} noValidate>
        <Grid container columnSpacing={4} rowSpacing={2}>
          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <TextField
              className="w-full"
              disabled={!enableEdit}
              name={'meritName'}
              required
              label={pageText.MERIT_NAME}
              InputLabelProps={{ shrink: true }}
              type="text"
              value={formik.values.meritName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.meritName && Boolean(formik.errors.meritName)
              }
              helperText={formik.touched.meritName && formik.errors.meritName}
            />
          </Grid>

          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <DatePicker
              className="w-full"
              disabled={!enableEdit}
              name="awardDate"
              required
              label={pageText.AWARD_DATE}
              InputLabelProps={{ shrink: true }}
              maxDate={dayjs()}
              value={
                formik.values.awardDate !== null
                  ? dayjs(formik.values.awardDate)
                  : null
              }
              onChange={date => {
                void formik.setFieldValue('awardDate', date);
              }}
              onBlur={formik.handleBlur}
              error={
                Boolean(formik.touched.awardDate) &&
                Boolean(formik.errors.awardDate)
              }
              helperText={
                Boolean(formik.touched.awardDate) && formik.errors.awardDate
              }
            />
          </Grid>

          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <Select
              className="w-full"
              disabled={!enableEdit}
              InputLabelProps={{ shrink: true }}
              name="country"
              id="country"
              required
              label={pageText.COUNTRY}
              options={nationalityOptions}
              value={formik.values.country ?? ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                Boolean(formik.touched.country) &&
                Boolean(formik.errors.country)
              }
              helperText={
                Boolean(formik.touched.country) && formik.errors.country
              }
            />
          </Grid>

          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <TextField
              className="w-full"
              disabled={!enableEdit}
              name="awardBodyName"
              required
              label={pageText.AWARD_BODY_NAME}
              InputLabelProps={{ shrink: true }}
              type="text"
              value={formik.values.awardBodyName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.awardBodyName &&
                Boolean(formik.errors.awardBodyName)
              }
              helperText={
                formik.touched.awardBodyName && formik.errors.awardBodyName
              }
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <TextField
              className="w-full"
              disabled={!enableEdit}
              name="attainmentMeans"
              required
              label={pageText.ATTAINMENT_MEANS}
              InputLabelProps={{ shrink: true }}
              type="text"
              value={formik.values.attainmentMeans}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.attainmentMeans &&
                Boolean(formik.errors.attainmentMeans)
              }
              helperText={
                formik.touched.attainmentMeans && formik.errors.attainmentMeans
              }
            />
          </Grid>
        </Grid>
        {enableEdit && (
          <Box display={'flex'} justifyContent={'center'} marginTop={2}>
            <Button
              style={{ marginRight: '15px' }}
              variant="contained"
              type="submit"
            >
              {PAGE_TEXT.SAVE}
            </Button>
            {enableDelete && (
              <Button onClick={props.handleDelete} variant="outlined">
                {PAGE_TEXT.DELETE}
              </Button>
            )}
          </Box>
        )}
      </form>
    </Container>
  );
};

const Container = styled.div`
  .MuiSwitch-root {
    margin: 1rem;
  }

  display: flex;
  flex-direction: column;
  margin-bottom: 2rem;
  width: 100%;

  .MuiSwitch-root {
    margin: 1rem;
  }

  form {
    margin-bottom: 2rem;
    width: 100%;
    padding-top: 0.5rem;
  }

  .MuiGrid-root .MuiGrid-item {
    margin-bottom: 0.5rem;
    display: flex;
    flex-direction: column;
    padding: 0.5rem 1rem;
  }

  .MuiInputBase-root {
    margin: 0 !important;
  }

  .input-area {
    display: flex;
    flex-direction: column;
  }

  .buttons-area {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    width: 100%;
    padding: 0.5rem 1rem;
    margin-top: 1rem;
    button {
      margin: 0 1rem;
    }
  }

  .MuiGrid-container {
    margin-top: 0;
    margin-left: 0;

    width: calc(100%);
  }

  .MuiGrid-root .MuiGrid-item {
    margin-bottom: 0.5rem;
    display: flex;
    flex-direction: column;
    padding: 0.5rem 1rem;
  }

  .MuiInputBase-root {
    margin: 0 !important;
  }

  .input-area {
    display: flex;
    flex-direction: column;
  }
`;
